import {
  ML_ROUTE_CUSTOM_LABEL,
  corridorRoutesForMlForm,
  filterRoutesForMlForm,
  formatRouteLabel,
  mlRouteDropdownEmptyMessage,
} from '../../utils/routeDisplay.js';
import {
  ML_MOBILE_DROPDOWN_PANEL,
  ML_MOBILE_DROPDOWN_SEARCH,
  ML_MOBILE_DROPDOWN_ITEM,
  ML_MOBILE_DROPDOWN_ITEM_TEXT,
  ML_MOBILE_FIELD_LABEL,
  ML_MOBILE_TRIGGER_TEXT,
  mlMobileTriggerClass,
} from '../../utils/mlSheetMobileDropdown.js';

/**
 * Route picker for ML create/edit. Always offers Custom (manual from/to).
 * When no saved route matches, Custom stays selectable with a clear hint.
 */
const MlRouteSelectField = ({
  routes = [],
  fromIds = [],
  toIds = [],
  routeId = '',
  locationMismatchWarning = null,
  parentMap = null,
  open = false,
  onOpenChange,
  searchTerm = '',
  onSearchChange,
  onSelectCustom,
  onSelectRoute,
  panelRef,
  variant = 'mobile',
  className = '',
  crossingHint = null,
  required = false,
}) => {
  const corridorRoutes = corridorRoutesForMlForm(routes, {
    fromIds,
    toIds,
    alwaysIncludeRouteId: routeId,
    parentMap,
  });
  const filtered = filterRoutesForMlForm(routes, {
    fromIds,
    toIds,
    searchTerm,
    alwaysIncludeRouteId: routeId,
    parentMap,
  });
  const hasCorridorMatch = corridorRoutes.length > 0;
  const showAllRoutesFallback =
    (fromIds.length > 0 || toIds.length > 0) && !hasCorridorMatch && routes.length > 0;
  const emptyMessage = mlRouteDropdownEmptyMessage(
    searchTerm,
    hasCorridorMatch,
    routes.length > 0,
  );
  const isCustom = !routeId;
  const selectedRoute = routes.find((r) => String(r.id) === String(routeId));
  const triggerLabel = routeId
    ? formatRouteLabel(selectedRoute) || ML_ROUTE_CUSTOM_LABEL
    : ML_ROUTE_CUSTOM_LABEL;

  const isDesktop = variant === 'desktop';
  const labelClass = isDesktop
    ? 'block text-sm font-semibold text-secondary mb-2'
    : ML_MOBILE_FIELD_LABEL;
  const triggerClass = isDesktop
    ? 'cursor-pointer border border-gray-300 rounded-xl px-3 py-2 bg-white flex justify-between items-center min-h-[42px]'
    : mlMobileTriggerClass(open);
  const triggerTextClass = isDesktop
    ? 'text-sm text-gray-700 truncate pr-2'
    : `${ML_MOBILE_TRIGGER_TEXT} whitespace-normal break-words line-clamp-2`;
  const panelClass = isDesktop
    ? 'absolute z-50 mt-1 left-0 right-0 bg-white border border-gray-300 rounded-xl shadow p-2'
    : ML_MOBILE_DROPDOWN_PANEL;
  const searchClass = isDesktop
    ? 'w-full px-3 py-2 mb-2 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 text-sm'
    : ML_MOBILE_DROPDOWN_SEARCH;
  const itemClass = isDesktop
    ? 'w-full text-left flex items-start gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer'
    : `${ML_MOBILE_DROPDOWN_ITEM} w-full text-left`;
  const itemTextClass = isDesktop
    ? 'text-sm text-gray-700 whitespace-normal break-words'
    : `${ML_MOBILE_DROPDOWN_ITEM_TEXT} whitespace-normal break-words`;

  const pickCustom = () => {
    onSelectCustom?.();
    onOpenChange?.(false);
    onSearchChange?.('');
  };

  return (
    <div className={`relative min-w-0 ${className}`} ref={panelRef}>
      <label className={labelClass}>
        Route
        {required ? (
          <span className="text-red-500 ml-1">*</span>
        ) : (
          <span className="text-gray-400 font-normal ml-1">(optional)</span>
        )}
      </label>
      <div
        onClick={() => onOpenChange?.(!open)}
        className={triggerClass}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onOpenChange?.(!open);
        }}
      >
        <span className={triggerTextClass}>{triggerLabel}</span>
        <span
          className={`shrink-0 text-[10px] sm:text-sm transition ${
            open ? 'rotate-180 text-blue-500' : 'text-gray-500'
          }`}
        >
          ▾
        </span>
      </div>
      {open && (
        <div className={panelClass}>
          <input
            type="text"
            placeholder="Search routes..."
            value={searchTerm}
            onChange={(e) => onSearchChange?.(e.target.value)}
            autoFocus
            className={searchClass}
          />
          <div className="max-h-48 overflow-y-auto">
            <button
              type="button"
              onClick={pickCustom}
              className={`${itemClass} ${isCustom ? 'bg-blue-50' : ''}`}
            >
              <span className={`${itemTextClass} ${isCustom ? 'text-blue-700 font-medium' : ''}`}>
                {ML_ROUTE_CUSTOM_LABEL}
              </span>
            </button>
            {showAllRoutesFallback && !searchTerm.trim() ? (
              <p className="text-[10px] sm:text-xs text-amber-800 px-2 py-1.5 border-b border-amber-100 bg-amber-50">
                No exact route for this From/To — select any saved route below or use Custom.
              </p>
            ) : null}
            {filtered.length > 0 ? (
              filtered.map((route) => {
                const isSelected = String(route.id) === String(routeId);
                return (
                  <button
                    key={route.id}
                    type="button"
                    onClick={() => {
                      onSelectRoute?.(route);
                      onOpenChange?.(false);
                      onSearchChange?.('');
                    }}
                    className={`${itemClass} ${isSelected ? 'bg-blue-50' : ''}`}
                  >
                    <span className={`${itemTextClass} ${isSelected ? 'text-blue-700' : ''}`}>
                      {formatRouteLabel(route)}
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="px-2 py-2 border-t border-gray-100 space-y-2">
                {emptyMessage ? (
                  <p className="text-[10px] sm:text-xs text-gray-600">{emptyMessage}</p>
                ) : null}
                <button
                  type="button"
                  onClick={pickCustom}
                  className={`${itemClass} bg-blue-50 border border-blue-100`}
                >
                  <span className={`${itemTextClass} text-blue-800 font-medium`}>
                    Use manual dispatch (no matching route)
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {locationMismatchWarning ? (
        <p className="text-[10px] sm:text-xs text-red-700 bg-red-50 border border-red-100 rounded-md px-2 py-1.5 mt-1 break-words">
          {locationMismatchWarning}
        </p>
      ) : isCustom && showAllRoutesFallback ? (
        <p className="text-[10px] sm:text-xs text-amber-800 mt-1 break-words">
          No exact route for this From/To — open Route and pick RT1/RT2, or keep Custom.
        </p>
      ) : (
        routeId && !locationMismatchWarning && (
          <p className="text-[10px] sm:text-xs text-green-600 mt-1 break-words">
            Route matched to these From/To locations. You can still change locations below.
          </p>
        )
      )}
      {crossingHint}
    </div>
  );
};

export default MlRouteSelectField;
